const MAX_TAKES = 64;
const MAX_BYTES = 4 * 1024 * 1024;
export interface DecodedAudioTake { buffer: AudioBuffer; hit: number; impact: number; onsets: number[]; nextOnsets: number[] }
interface CachedTake { pending: Promise<DecodedAudioTake>; bufferPending: Promise<AudioBuffer>; take?: DecodedAudioTake }
type AudioTakePage = typeof globalThis & { __ftDecodedAudioTakes?: Map<string, CachedTake> };

// Bundle copies share one bounded cache. Effect timing never holds a second strong reference cache.
function takeCache(): Map<string, CachedTake> {
    return (globalThis as AudioTakePage).__ftDecodedAudioTakes ??= new Map();
}

/** Shared decode cache; an offline context needs no autoplay gesture. */
function cachedTake(url: string): CachedTake {
    const decoded = takeCache();
    const cached = decoded.get(url);
    if (cached) { decoded.delete(url); decoded.set(url, cached); return cached; }
    const pending = (async () => {
        const response = await fetch(url, { signal: AbortSignal.timeout(4000) });
        if (!response.ok) throw new Error('audio take unavailable');
        if (Number(response.headers.get('content-length')) > MAX_BYTES) throw new Error('audio take exceeds budget');
        const bytes = await response.arrayBuffer();
        if (bytes.byteLength > MAX_BYTES) throw new Error('audio take exceeds budget');
        const buffer = await new OfflineAudioContext(1, 1, 44100).decodeAudioData(bytes);
        const channels = Array.from({ length: buffer.numberOfChannels }, (_, c) => buffer.getChannelData(c));
        const levels = windowLevels(channels, buffer.sampleRate);
        const steps = stepOnsets(levels, buffer.sampleRate, buffer.duration);
        return { buffer, hit: hitSeconds(levels, buffer.sampleRate), impact: impactSeconds(levels, buffer.sampleRate),
            onsets: steps.onsets, nextOnsets: steps.nextOnsets };
    })();
    const entry: CachedTake = { pending, bufferPending: pending.then(take => take.buffer) };
    decoded.set(url, entry);
    void entry.pending.then(take => { entry.take = take; }, () => { if (decoded.get(url) === entry) decoded.delete(url); });
    void entry.bufferPending.catch(() => {});
    if (decoded.size > MAX_TAKES) decoded.delete(decoded.keys().next().value!);
    return entry;
}

export function decodeAudioTake(url: string): Promise<AudioBuffer> { return cachedTake(url).bufferPending; }
export function decodeAudioTakeInfo(url: string): Promise<DecodedAudioTake> { return cachedTake(url).pending; }

/** A cold contact warms its take and stays silent instead of playing late; scans finish with decoding. */
export function peekDecodedAudioTakeInfo(url: string): DecodedAudioTake | null {
    const decoded = takeCache();
    const entry = decoded.get(url);
    if (entry) { decoded.delete(url); decoded.set(url, entry); }
    return entry?.take ?? null;
}

/** Decoded and ready, without touching the cache's recency (a warm check must not protect unplayed takes). */
export function hasDecodedAudioTake(url: string): boolean { return !!takeCache().get(url)?.take; }

export function peekDecodedAudioTake(url: string): AudioBuffer | null { return peekDecodedAudioTakeInfo(url)?.buffer ?? null; }

/** Peak level per 10 ms window across decoded channels. */
export function windowLevels(channels: Float32Array[], sampleRate: number): { levels: number[]; window: number; loudest: number } {
    const window = Math.max(1, Math.round(sampleRate * 0.01));
    const length = channels[0]?.length ?? 0;
    const levels: number[] = [];
    let loudest = 0;
    for (let start = 0; start < length; start += window) {
        let peak = 0;
        for (const data of channels) for (let i = start, end = Math.min(data.length, start + window); i < end; i++) {
            peak = Math.max(peak, Math.abs(data[i]!));
        }
        levels.push(peak); loudest = Math.max(loudest, peak);
    }
    return { levels, window, loudest };
}

type WindowLevels = ReturnType<typeof windowLevels>;

function hitSeconds({ levels, window, loudest }: WindowLevels, sampleRate: number): number {
    if (loudest < 1e-4) return 0;
    const index = levels.findIndex(level => level >= loudest * 0.5);
    return Math.max(0, index) * window / sampleRate;
}

/** Pure: the hit time in seconds of decoded channel data (exported for tests). */
export function takeHitSeconds(channels: Float32Array[], sampleRate: number): number {
    return hitSeconds(windowLevels(channels, sampleRate), sampleRate);
}

/** A later burst after a dip supplies projectile impact timing; otherwise keep the first hit. */
function impactSeconds({ levels, window, loudest }: WindowLevels, sampleRate: number): number {
    if (loudest < 1e-4) return 0;
    const hit = Math.max(0, levels.findIndex(level => level >= loudest * 0.5));
    const toSeconds = (index: number) => index * window / sampleRate;
    const later = levels.findIndex((level, i) => i >= hit + 15 && level >= loudest * 0.7);
    if (later < 0) return toSeconds(hit);
    let dip = hit, hitBurst = 0;
    for (let i = hit; i <= later; i++) if (levels[i]! < levels[dip]!) dip = i;
    if (levels[dip]! > loudest * 0.5) return toSeconds(hit);
    for (let i = hit; i < dip; i++) hitBurst = Math.max(hitBurst, levels[i]!);
    let eventPeak = 0, end = later;
    while (end < levels.length && levels[end]! >= loudest * 0.5) { eventPeak = Math.max(eventPeak, levels[end]!); end++; }
    if (eventPeak < hitBurst) return toSeconds(hit);
    for (let i = dip; i <= later; i++) if (levels[i]! >= eventPeak * 0.5) return toSeconds(i);
    return toSeconds(later);
}

/**
 * Pure: where a take's IMPACT is — what a PROJECTILE lines up with its arrival (2026-10-07). Usually the hit.
 * But the cast-SFX template opens with a launch whoosh loud enough to be detected as the hit, and its real
 * impact (the core layer) follows after a dip: an audit of the approved projectile pools found 123/255 takes
 * shaped like that, landing their burst ~230 ms after the VFX impact. So when, at least 150 ms past the hit,
 * the level DIPS below half-max and a later event comes back at >= 70 % of the loudest AND at least as loud as
 * the hit's own burst, the impact is that event's onset (its first window at half its own peak). Anything
 * else — a single burst, a sustained body, a second burst inside the first — keeps impact = hit.
 */
export function takeImpactSeconds(channels: Float32Array[], sampleRate: number): number {
    return impactSeconds(windowLevels(channels, sampleRate), sampleRate);
}

function stepOnsets(analysis: WindowLevels, sampleRate: number, duration: number): { onsets: number[]; nextOnsets: number[] } {
    const { levels, window, loudest } = analysis;
    const onsets: number[] = [];
    if (loudest >= 1e-4) for (let i = 0; i < levels.length; i++) {
        if (levels[i]! < loudest * .45 || (i > 0 && levels[i - 1]! >= loudest * .45)) continue;
        const time = i * window / sampleRate;
        if (!onsets.length || time - onsets[onsets.length - 1]! >= .15 - 1e-6) onsets.push(time);
    }
    if (!onsets.length) return { onsets: [hitSeconds(analysis, sampleRate)], nextOnsets: [Infinity] };
    const usable = onsets.filter(time => duration - time >= .15 - 1e-6);
    const selected = usable.length ? usable : [onsets[0]!];
    // An omitted tail strike still bounds its neighbour's slice so it cannot play as a second step.
    return { onsets: selected, nextOnsets: selected.map(time => onsets[onsets.indexOf(time) + 1] ?? Infinity) };
}

/** Pure rising crossings of 45% peak, merging bursts within 150 ms and omitting truncated tails. */
export function stepOnsetSeconds(channels: Float32Array[], sampleRate: number): number[] {
    return stepOnsets(windowLevels(channels, sampleRate), sampleRate, (channels[0]?.length ?? 0) / sampleRate).onsets;
}
